// modules/reporteCliente/pages/ReporteClienteDetalle.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box, Typography, Button, Alert, CircularProgress, LinearProgress,
  Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, Grid, Tabs, Tab,
  TextField, MenuItem, Snackbar, TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SendIcon         from "@mui/icons-material/Send";
import ReceiptIcon      from "@mui/icons-material/Receipt";
import DescriptionIcon  from "@mui/icons-material/Description";
import VisibilityIcon   from "@mui/icons-material/Visibility";
import GppMaybeIcon     from "@mui/icons-material/GppMaybe";
import DashboardLayout from "../../../layouts/DashboardLayout";
import axios from "../../../config/axiosConfig";
import PrefacturaServicioModal from "../../facturacion/components/PrefacturaServicioModal";
import DetalleFacturaModal from "../../facturacion/components/DetalleFacturaModal";
import PageHeader from "../../../components/layout/PageHeader";
import HistorialCliente from "../../historialCliente/pages/HistorialCliente";
import { useAuth } from "../../../context/AuthContext";
import { getPagoLabel } from "../../clientes/constants/pago";

const Th = styled(TableCell)({
  fontWeight: 600,
  backgroundColor: "#008080",
  color: "white",
  padding: "3px 6px",
  fontSize: "0.78rem",
  whiteSpace: "nowrap",
});

const MONEDAS  = { 1: "Dólares", 2: "Bolivianos" };
const IND      = { si: "Individual", no: "Masiva" };
const TIPO_FAC = { si: "Pre-Factura", no: "Factura" };
const TIPOS_INGRESO = {
  1: "Servicios",
  2: "Franquicia",
  3: "Intercambio Serv.",
  4: "Castigo Deuda",
};
const TIPOS_PAGO = { 1: "Dolares", 2: "Bolivianos" };

const today = () => new Date().toISOString().slice(0, 10);
const pagoFieldSx = {
  "& .MuiInputBase-root": { minHeight: 31, fontSize: "0.76rem" },
  "& .MuiInputBase-input": { py: 0.45, px: 0.9 },
  "& .MuiInputLabel-root": { fontSize: "0.72rem", top: -2 },
  "& .MuiInputLabel-shrink": { top: 0 },
  "& .MuiSelect-select": { py: "4px" },
};

const fmtMonto = (n) => Number(n || 0).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const fmtFecha = (f) => {
  if (!f || f === "0000-00-00") return "?";

  const str = String(f);
  const parsed = new Date(str.includes("T") ? str : str.replace(" ", "T"));

  if (!Number.isNaN(parsed.getTime())) {
    const parts = new Intl.DateTimeFormat("es-BO", {
      timeZone: "America/La_Paz",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(parsed).reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

    return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}:${parts.second}`;
  }

  const [datePart, timePart = "00:00:00"] = str.split(/[ T]/);
  const [y, m, d] = datePart.split("-");
  return y && m && d ? `${d}/${m}/${y} ${timePart.slice(0, 8)}` : str;
};

const Campo = ({ label, value }) => (
  <Box
    sx={{
      height: "100%",
      minHeight: 48,
      px: 0.9,
      py: 0.65,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      bgcolor: "background.paper",
      overflow: "hidden",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.1, fontSize: "0.67rem", mb: 0.2 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.78rem", lineHeight: 1.2, overflowWrap: "anywhere" }}>
      {value || "-"}
    </Typography>
  </Box>
);

// ── Tabs de vista ─────────────────────────────────────────────
const TABS = [
  { value: "todo",    label: "Pre-Fac/Facturas" },
  { value: "emitida", label: "Pre-Facturas" },
  { value: "factura", label: "Facturas" },
  { value: "registrar-pago", label: "Registrar pago" },
  { value: "pagos-sin-factura", label: "Pagos sin factura" },
  { value: "pagos-con-factura", label: "Pagos con factura" },
];

const PagoServicioPanel = ({ codServicio, servicio, tab, onRegistered }) => {
  const { user } = useAuth();
  const [bancos, setBancos] = useState([]);
  const [pagos, setPagos] = useState({ sinFactura: [], conFactura: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [form, setForm] = useState({
    pago_moneda: "bolivianos",
    tipo_ingreso: "2",
    forma_pago: "Efectivo",
    cobrador: user?.username || "",
    sucursal: user?.sucursal || servicio?.sucursal_facturar || "",
    moneda: "",
    monto: "",
    fecha_pago: today(),
    numrecibo: "",
    numcheque: "",
    razonpago: "",
    banco_deposito: "",
    cod_banco: "",
    fecha_deposito: today(),
    obs: "",
  });

  const showToast = (message, severity = "success") => setToast({ open: true, message, severity });
  const handleForm = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const cargarPagos = useCallback(async () => {
    if (!codServicio) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/facturas/reporte/${codServicio}/pagos`);
      const payload = res.data || { sinFactura: [], conFactura: [] };
      setPagos(payload);
      if (payload.tipoCambio) {
        setForm((prev) => prev.moneda ? prev : { ...prev, moneda: String(payload.tipoCambio) });
      }
    } catch (e) {
      showToast(e?.response?.data?.message || "No se pudieron cargar los pagos", "error");
    } finally {
      setLoading(false);
    }
  }, [codServicio]);

  useEffect(() => {
    let mounted = true;
    axios.get("/api/bancos", { params: { propio: 1, limit: 200 } })
      .then((res) => { if (mounted) setBancos(Array.isArray(res.data) ? res.data : res.data?.data || []); })
      .catch(() => {});
    axios.get("/api/facturas/tipo-cambio")
      .then((res) => {
        const cambio = res.data?.cambio;
        if (mounted && cambio) {
          setForm((prev) => prev.moneda ? prev : { ...prev, moneda: String(cambio) });
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => { cargarPagos(); }, [cargarPagos]);

  const guardarPago = async () => {
    if (!form.monto || Number(form.monto) <= 0) return showToast("Ingrese un monto valido", "warning");
    if (!form.tipo_ingreso) return showToast("Seleccione el tipo de ingreso", "warning");
    if (!form.banco_deposito) return showToast("Seleccione el banco", "warning");
    if (!String(form.cod_banco || "").trim()) return showToast("Ingrese el codigo de banco", "warning");

    setSaving(true);
    try {
      const payload = { ...form, banco_pago: form.banco_deposito };
      const res = await axios.post(`/api/facturas/reporte/${codServicio}/pagos/manual`, payload);
      setPagos(res.data?.pagos || pagos);
      setForm((prev) => ({
        ...prev,
        monto: "",
        numrecibo: "",
        numcheque: "",
        razonpago: "",
        cod_banco: "",
        obs: "",
      }));
      onRegistered?.();
      showToast("Pago registrado");
    } catch (e) {
      showToast(e?.response?.data?.message || "No se pudo registrar el pago", "error");
    } finally {
      setSaving(false);
    }
  };

  const renderPagos = (rows, { conFactura = false } = {}) => (
    <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
      <Table size="small" sx={{ "& td": { py: 0.3, px: 0.6, fontSize: "0.74rem" } }}>
        <TableHead>
          <TableRow>
            {conFactura && <Th>Nro Factura</Th>}
            <Th>Forma</Th>
            <Th>Moneda</Th>
            <Th>Cod Banco</Th>
            <Th>Cheque</Th>
            <Th>Recibo</Th>
            <Th align="right">Monto</Th>
            <Th>Banco Pago</Th>
            <Th>Fecha Pago</Th>
            <Th>Banco Deposito</Th>
            <Th>Fecha Deposito</Th>
            <Th>Razon</Th>
            <Th>Tipo Pago</Th>
            <Th>Tipo Ingreso</Th>
            <Th>Observacion</Th>
            <Th>Cobrador</Th>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={conFactura ? 15 : 14}><CircularProgress size={16} /></TableCell></TableRow>
          ) : rows?.length ? rows.map((p) => (
            <TableRow key={p.cod} hover>
              {conFactura && <TableCell>{p.fact || p.coddos || "-"}</TableCell>}
              <TableCell>{p.forma_pago || "-"}</TableCell>
              <TableCell>
                {String(p.forma_pago || "").toLowerCase() === "deposito" && (!Number(p.moneda) || Number(p.moneda) === 1)
                  ? (pagos.tipoCambio || p.moneda || "-")
                  : (p.moneda || "-")}
              </TableCell>
              <TableCell>{p.cod_banco || "-"}</TableCell>
              <TableCell>{p.numcheque || "-"}</TableCell>
              <TableCell>{p.numrecibo || "-"}</TableCell>
              <TableCell align="right">{fmtMonto(p.monto)}</TableCell>
              <TableCell>{p.banco_pago_nombre || p.banco_pago || "-"}</TableCell>
              <TableCell>{fmtFecha(p.fecha_pago)}</TableCell>
              <TableCell>{p.banco_deposito_nombre || p.banco_deposito || "-"}</TableCell>
              <TableCell>{fmtFecha(p.fecha_deposito)}</TableCell>
              <TableCell>{p.razonpago || "-"}</TableCell>
              <TableCell>{TIPOS_PAGO[p.tipo_pago] || p.tipo_pago || "-"}</TableCell>
              <TableCell>{TIPOS_INGRESO[p.tipo_ingreso] || p.tipo_ingreso || "-"}</TableCell>
              <TableCell>{p.obs || "-"}</TableCell>
              <TableCell>{p.cobrador || "-"}</TableCell>
            </TableRow>
          )) : (
            <TableRow>
              <TableCell colSpan={conFactura ? 15 : 14} align="center" sx={{ py: 1.5, color: "text.secondary" }}>
                No existen pagos registrados
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
        {tab === "registrar-pago" && (
          <Grid container spacing={0.5} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 6, md: 1.25 }}>
              <TextField sx={pagoFieldSx} select fullWidth size="small" label="Pago en" value={form.pago_moneda} onChange={(e) => handleForm("pago_moneda", e.target.value)}>
                <MenuItem value="bolivianos">Bolivianos</MenuItem>
                <MenuItem value="dolares">Dolares</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 1.65 }}>
              <TextField sx={pagoFieldSx} select fullWidth size="small" label="Tipo ingreso" value={form.tipo_ingreso} onChange={(e) => handleForm("tipo_ingreso", e.target.value)}>
                {Object.entries(TIPOS_INGRESO).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 1.25 }}>
              <TextField sx={pagoFieldSx} select fullWidth size="small" label="Forma" value={form.forma_pago} onChange={(e) => handleForm("forma_pago", e.target.value)}>
                <MenuItem value="Efectivo">Efectivo</MenuItem>
                <MenuItem value="Cheque">Cheque</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 1.25 }}>
              <TextField sx={pagoFieldSx} fullWidth size="small" label="Cobrador" value={form.cobrador} onChange={(e) => handleForm("cobrador", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, md: 1 }}>
              <TextField sx={pagoFieldSx} fullWidth size="small" label="Cambio" value={form.moneda} onChange={(e) => handleForm("moneda", e.target.value)} inputProps={{ inputMode: "decimal" }} />
            </Grid>
            <Grid size={{ xs: 6, md: 1.15 }}>
              <TextField sx={pagoFieldSx} required fullWidth size="small" label="Monto" value={form.monto} onChange={(e) => handleForm("monto", e.target.value)} inputProps={{ inputMode: "decimal" }} />
            </Grid>
            <Grid size={{ xs: 6, md: 1.3 }}>
              <TextField sx={pagoFieldSx} fullWidth size="small" type="date" label="Fecha cobro" value={form.fecha_pago} onChange={(e) => handleForm("fecha_pago", e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 1.1 }}>
              <TextField sx={pagoFieldSx} fullWidth size="small" label="Recibo" value={form.numrecibo} onChange={(e) => handleForm("numrecibo", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, md: 1.1 }}>
              <TextField sx={pagoFieldSx} fullWidth size="small" label="Cheque" value={form.numcheque} onChange={(e) => handleForm("numcheque", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, md: 1.65 }}>
              <TextField sx={pagoFieldSx} fullWidth size="small" label="Razon del pago" value={form.razonpago} onChange={(e) => handleForm("razonpago", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, md: 1.8 }}>
              <TextField sx={pagoFieldSx} required select fullWidth size="small" label="Banco" value={form.banco_deposito} onChange={(e) => handleForm("banco_deposito", e.target.value)}>
                {bancos.map((b) => <MenuItem key={b.id} value={b.id}>{b.nombre}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 1.25 }}>
              <TextField sx={pagoFieldSx} required fullWidth size="small" label="Cod. banco" value={form.cod_banco} onChange={(e) => handleForm("cod_banco", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 6, md: 1.3 }}>
              <TextField sx={pagoFieldSx} fullWidth size="small" type="date" label="Fecha deposito" value={form.fecha_deposito} onChange={(e) => handleForm("fecha_deposito", e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, md: 2.7 }}>
              <TextField sx={pagoFieldSx} fullWidth size="small" label="Observaciones" value={form.obs} onChange={(e) => handleForm("obs", e.target.value)} />
            </Grid>
            <Grid size={{ xs: 12, md: 1.15 }}>
              <Button fullWidth variant="contained" color="success" size="small" sx={{ height: 31, fontSize: "0.72rem", px: 1 }} onClick={guardarPago} disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </Grid>
          </Grid>
        )}
        {tab === "pagos-sin-factura" && renderPagos(pagos.sinFactura)}
        {tab === "pagos-con-factura" && renderPagos(pagos.conFactura, { conFactura: true })}

      <Snackbar open={toast.open} autoHideDuration={3000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.severity} sx={{ width: "100%" }}>{toast.message}</Alert>
      </Snackbar>
    </>
  );
};

const ReporteClienteDetalle = () => {
  const { cod_servicio_cliente } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = location.state?.from || "/reporte-cliente";
  // Si llegamos desde el Resumen, al volver hay que reenviar el origen original
  // (ej. /mis-clientes) para que el propio botón "Atrás" del Resumen no lo pierda.
  const backOrigin = location.state?.origin;

  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [tabVista,  setTabVista]  = useState("todo");
  const [facturaPage, setFacturaPage] = useState(0);
  const [facturaPageSize, setFacturaPageSize] = useState(20);
  const [modalPref, setModalPref] = useState(null);
  const [facturaVerCod, setFacturaVerCod] = useState(null);
  const [emitiendo, setEmitiendo] = useState("");
  const [mainTab, setMainTab] = useState("reporte");

  // Solo las pestañas de listado de facturas necesitan recargar el reporte;
  // cambiar entre las pestañas de pagos no debe disparar un nuevo fetch.
  const preFac = useMemo(() => {
    if (tabVista === "emitida") return "si";
    if (tabVista === "factura") return "no";
    return "";
  }, [tabVista]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/facturas/reporte/${cod_servicio_cliente}`, {
        params: {
          page: facturaPage + 1,
          pageSize: facturaPageSize,
          ...(preFac ? { pre_fac: preFac } : {}),
          ...(tabVista === "todo" ? { excluir_anuladas: 1 } : {}),
        },
      });
      const raw = res.data?.data ?? res.data;
      setData(raw);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [cod_servicio_cliente, facturaPage, facturaPageSize, preFac, tabVista]);

  useEffect(() => { cargar(); }, [cargar]);

  const emitirPrefactura = async (factura) => {
    if (!factura?.cod) return;
    if (!window.confirm(`Desea emitir la prefactura #${factura.coddos || factura.cod}?`)) return;

    setEmitiendo(String(factura.cod));
    setError(null);
    try {
      await axios.post(`/api/facturas/${factura.cod}/emitir`, {});
      await cargar();
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudo emitir la prefactura");
    } finally {
      setEmitiendo("");
    }
  };

  // Filtrar facturas según tab activo
  const facturasFiltradas = data?.facturas || [];

  if (loading && !data) return (
    <DashboardLayout>
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    </DashboardLayout>
  );

  if (error && !data) return (
    <DashboardLayout>
      <Box sx={{ p: 1 }}><Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert></Box>
    </DashboardLayout>
  );

  const { servicio } = data || {};

  return (
    <DashboardLayout>
      <PageHeader
        title={servicio?.razon_social || "Detalle del Cliente"}
        subtitle={`Servicio #${servicio?.cod_servicio_cliente || cod_servicio_cliente}${servicio?.descripcion ? ` · ${servicio.descripcion}` : ""}`}
        backTo={backTo}
        onBack={backOrigin ? () => navigate(backTo, { state: { from: backOrigin } }) : undefined}
        actions={
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon fontSize="small" />}
            onClick={() => navigate(`/reporte-cliente/${cod_servicio_cliente}/resumen`, {
              state: { from: `/reporte-cliente/${cod_servicio_cliente}`, origin: backOrigin || backTo },
            })}
          >
            Ver Resumen
          </Button>
        }
      />
      <Box sx={{ p: 1 }}>
        {/* ── Pestañas principales ── */}
        <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden" }}>
          <Tabs
            value={mainTab}
            onChange={(_, v) => setMainTab(v)}
            sx={{
              minHeight: 34,
              "& .MuiTab-root": {
                minHeight: 34,
                py: 0.5,
                px: 1.25,
                fontSize: 12,
                textTransform: "none",
              },
            }}
          >
            <Tab value="reporte" label="Reporte cliente" />
            <Tab value="detalle" label="Detalle Cliente" />
          </Tabs>
        </Paper>

        <Box sx={{ display: mainTab === "detalle" ? "block" : "none" }}>
          <HistorialCliente
            codServicioClienteProp={cod_servicio_cliente}
            embedded
            onBack={() => setMainTab("reporte")}
          />
        </Box>

        <Box sx={{ display: mainTab === "reporte" ? "block" : "none" }}>
        {/* ── Header ── */}
        {/* ── Central de riesgo: alerta visible antes de cualquier otro dato ── */}
        {servicio?.central_riesgo && servicio.central_riesgo !== "No" && (
          <Box
            role="alert"
            sx={{
              display: "flex", alignItems: "center", gap: 1.25, mb: 1, px: 1.5, py: 0.9, borderRadius: 1.25,
              color: "#3b2a00", bgcolor: "#ffd54f", border: "1px solid #c79100", boxShadow: "0 2px 6px rgba(199,145,0,0.35)",
            }}
          >
            <GppMaybeIcon sx={{ fontSize: 30 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", opacity: 0.9, lineHeight: 1.2 }}>
                Central de riesgo
              </Typography>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, lineHeight: 1.25 }}>
                {servicio.central_riesgo}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Info del servicio ── */}
        <Paper variant="outlined" sx={{ p: 0.75, mb: 1, bgcolor: "background.paper", borderRadius: 1.25 }}>
          <Grid container spacing={0.75} alignItems="stretch">
            <Grid size={{ xs: 12, md: 4 }}>
              <Campo label="Empresa" value={servicio?.razon_social} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Campo label="Descripcion" value={servicio?.descripcion} />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Campo label="Nro. Servicio" value={servicio?.cod_servicio_cliente} />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Campo label="Moneda" value={MONEDAS[servicio?.moneda] || "-"} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Campo label="Servicio" value={servicio?.servicio_nombre} />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Campo label="Estado" value={servicio?.estado} />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Campo label="Ciudad" value={servicio?.sucursal_facturar} />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Campo label="Tipo" value={servicio?.tipo} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Campo label="Forma de Pago"
                value={servicio?.pago ? getPagoLabel(servicio.pago) : "-"} />
            </Grid>
            {servicio?.observacion && (
              <Grid size={{ xs: 12 }}>
                <Campo label="Observacion" value={servicio.observacion} />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* ── Botones de acción ── */}
        <Box sx={{ display: "flex", gap: 0.75, mb: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined" size="small" color="warning"
            startIcon={<DescriptionIcon />}
            onClick={() => setModalPref({ tipo: "pre" })}>
            Nueva Pre-Factura
          </Button>
          <Button
            variant="contained" size="small" color="primary"
            startIcon={<ReceiptIcon />}
            onClick={() => setModalPref({ tipo: "fac" })}>
            Nueva Factura
          </Button>
        </Box>

        {/* ── Tabs de filtro ── */}
        <Paper>
          <Tabs
            value={tabVista}
            onChange={(_, v) => {
              setTabVista(v);
              setFacturaPage(0);
            }}
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              px: 1,
              minHeight: 34,
              "& .MuiTab-root": {
                minHeight: 34,
                py: 0.5,
                px: 1.25,
                fontSize: 12,
                textTransform: "none",
              },
            }}
          >
            {TABS.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>

          <Box sx={{ p: 1 }}>
            {["todo", "emitida", "factura"].includes(tabVista) ? (
            <>
            {loading && <LinearProgress sx={{ mb: 1 }} />}
            {error && <Alert severity="error" sx={{ mb: 1, py: 0.5 }}>{error}</Alert>}
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.25, px: 0.75, fontSize: "0.78rem" } }}>
                <TableHead>
                  <TableRow>
                    <Th>N° Pre-Fac</Th>
                    <Th>Fecha Emi.</Th>
                    <Th>N° Factura</Th>
                    <Th>Fecha Fac.</Th>
                    <Th>Tipo</Th>
                    <Th>Emitido</Th>
                    <Th>Estado</Th>
                    <Th>Mes/Año</Th>
                    <Th align="right">Cant.</Th>
                    <Th align="right">Bs.</Th>
                    <Th align="right">$us</Th>
                    <Th align="right">Saldo</Th>
                    <Th align="center">Acciones</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!facturasFiltradas.length ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 1.5 }}>
                        <Typography color="text.secondary" variant="body2">
                          No hay registros
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    facturasFiltradas.map((f) => {
                      const pagada = f.pagada === "si";
                      return (
                        <TableRow
                          key={f.cod}
                          hover
                          sx={(theme) => ({
                            backgroundColor: f.estado === "X"
                              ? (theme.palette.mode === "dark" ? "rgba(239, 68, 68, 0.16)" : "#fee2e2")
                              : pagada
                                ? (theme.palette.mode === "dark" ? "rgba(34, 197, 94, 0.18)" : "#6F9")
                                : "inherit",
                        
                            opacity: f.estado === "X" ? 0.75 : 1,
                        
                            "&.MuiTableRow-hover:hover": {
                              backgroundColor: f.estado === "X"
                                ? (theme.palette.mode === "dark" ? "rgba(239, 68, 68, 0.26)" : "#fecaca")
                                : pagada
                                  ? (theme.palette.mode === "dark" ? "rgba(34, 197, 94, 0.28)" : "#91d38f")
                                  : theme.palette.action.hover,
                            },
                        
                            "&.MuiTableRow-hover:hover > td": {
                              backgroundColor: f.estado === "X"
                                ? (theme.palette.mode === "dark" ? "rgba(239, 68, 68, 0.26)" : "#fecaca")
                                : pagada
                                  ? (theme.palette.mode === "dark" ? "rgba(34, 197, 94, 0.28)" : "#53bc78")
                                  : theme.palette.action.hover,
                            },
                          })}
                        >
                          <TableCell sx={{ color: "error.main", fontWeight: 600 }}>
                            {f.coddos}
                          </TableCell>
                          <TableCell>{fmtFecha(f.fecha_emi)}</TableCell>
                          <TableCell>{f.num_fac || "—"}</TableCell>
                          <TableCell>{fmtFecha(f.fecha_fac)}</TableCell>
                          <TableCell>
                            {f.venta === "Venta"
                              ? <strong>{f.venta}</strong> : f.venta}
                          </TableCell>
                          <TableCell>{IND[f.fac_ind] || f.fac_ind}</TableCell>
                          <TableCell>
                            {f.estado === "X" ? (
                              <Chip label="Anulada" size="small" color="error" sx={{ height: 20, fontSize: 11 }} />
                            ) : (
                              <Chip
                                label={TIPO_FAC[f.pre_fac] || f.pre_fac}
                                size="small"
                                color={f.pre_fac === "si" ? "warning" : "success"}
                                sx={{ height: 20, fontSize: 11 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {f.year && Number(f.year) > 0
                              ? `${f.mes} de ${f.year}` : f.mes}
                          </TableCell>
                          <TableCell align="right">{f.cantidad}</TableCell>
                          <TableCell align="right">{fmtMonto(f.monto_total)}</TableCell>
                          <TableCell align="right">{fmtMonto(f.dolares)}</TableCell>
                          <TableCell align="right">{fmtMonto(f.saldo)}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, flexWrap: "wrap" }}>
                              <Button size="small" variant="outlined" color="info"
                                startIcon={<VisibilityIcon />}
                                sx={{ minHeight: 26, fontSize: "0.72rem", px: 0.75 }}
                                onClick={() => setFacturaVerCod(f.cod)}>
                                Ver
                              </Button>
                              {f.pre_fac === "si" && f.estado !== "X" && (
                                <Button size="small" variant="outlined" color="primary"
                                  startIcon={<SendIcon />}
                                  sx={{ minHeight: 26, fontSize: "0.72rem", px: 0.75 }}
                                  disabled={emitiendo === String(f.cod)}
                                  onClick={() => emitirPrefactura(f)}>
                                  {emitiendo === String(f.cod) ? "Emitiendo..." : "Emitir"}
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={Number(data?.pagination?.total || 0)}
              page={facturaPage}
              rowsPerPage={facturaPageSize}
              rowsPerPageOptions={[10, 20, 50, 100]}
              labelRowsPerPage="Limite"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              onPageChange={(_, nextPage) => setFacturaPage(nextPage)}
              onRowsPerPageChange={(e) => {
                setFacturaPageSize(Number(e.target.value));
                setFacturaPage(0);
              }}
              sx={{
                minHeight: 36,
                "& .MuiTablePagination-toolbar": { minHeight: 36 },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: "0.76rem",
                },
              }}
            />
            </>
            ) : (
              <PagoServicioPanel
                codServicio={cod_servicio_cliente}
                servicio={servicio}
                tab={tabVista}
                onRegistered={() => setTabVista("pagos-sin-factura")}
              />
            )}
          </Box>
        </Paper>
        </Box>

        {/* ── Modales ── */}
        {modalPref && (
          <PrefacturaServicioModal
            open={!!modalPref}
            onClose={() => setModalPref(null)}
            onSuccess={() => { setModalPref(null); cargar(); }}
            servicio={{
              cod_servicio_cliente : Number(cod_servicio_cliente),
              cod_cliente          : servicio?.cod_cliente,
              cod_servicio         : servicio?.cod_servicio,
              razon_social         : servicio?.razon_social,
              servicio_nombre      : servicio?.servicio_nombre,
              descripcion          : servicio?.descripcion,
              sucursal_facturar    : servicio?.sucursal_facturar,
              moneda               : servicio?.moneda,
              pago                 : servicio?.pago,
            }}
            tipo={modalPref.tipo}
            individual={true}
          />
        )}

        {facturaVerCod && (
          <DetalleFacturaModal
            open={!!facturaVerCod}
            cod={facturaVerCod}
            onClose={() => setFacturaVerCod(null)}
            onSuccess={cargar}
          />
        )}

      </Box>
    </DashboardLayout>
  );
};

export default ReporteClienteDetalle;
